import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  MOBILE_APP_STORE_URL,
  MOBILE_DEEP_LINK_SCHEME,
  MOBILE_PLAY_STORE_URL,
} from '@web/constants/app';

const SESSION_KEY = 'mobileAppBannerDismissed';
const APP_OPEN_TIMEOUT_MS = 1500;

type MobileOS = 'ios' | 'android' | null;

const detectMobileOS = (): MobileOS => {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return null;
};

const buildDeepLink = (path: string): string => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${MOBILE_DEEP_LINK_SCHEME}${cleanPath}`;
};

const storeURLForOS = (os: MobileOS): string =>
  os === 'ios' ? MOBILE_APP_STORE_URL : MOBILE_PLAY_STORE_URL;

export const MobileAppBanner = (): ReactElement | null => {
  const theme = useTheme();
  const location = useLocation();

  const [mobileOS] = useState<MobileOS>(detectMobileOS);
  const [dismissed, setDismissed] = useState<boolean>(
    () => sessionStorage.getItem(SESSION_KEY) === 'true',
  );

  // Swipe-up-to-dismiss state
  const touchStartY = useRef<number | null>(null);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, 'true');
    setDismissed(true);
  }, []);

  const openInApp = useCallback(() => {
    const deepLink = buildDeepLink(location.pathname + location.search);
    const storeURL = storeURLForOS(mobileOS);

    // Attempt to open the native app. If it is not installed the browser
    // will stay on this page, so after a short delay redirect to the store.
    window.location.href = deepLink;

    const timer = window.setTimeout(() => {
      window.location.href = storeURL;
    }, APP_OPEN_TIMEOUT_MS);

    // If the page becomes hidden the app opened successfully – cancel the store redirect.
    const handleVisibilityChange = (): void => {
      if (document.hidden) {
        clearTimeout(timer);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }, [location.pathname, location.search, mobileOS]);

  useEffect(() => {
    const el = bannerRef.current;
    if (el === null) return undefined;

    const onTouchStart = (e: TouchEvent): void => {
      touchStartY.current = e.touches[0].clientY;
    };

    const onTouchEnd = (e: TouchEvent): void => {
      if (touchStartY.current === null) return;
      const delta = e.changedTouches[0].clientY - touchStartY.current;
      if (delta < -60) dismiss(); // swiped up ≥ 60px
      touchStartY.current = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return (): void => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [dismiss]);

  if (mobileOS === null || dismissed) return null;

  return (
    <Box
      ref={bannerRef}
      role='dialog'
      aria-modal='true'
      aria-label='Open in the Slabstack app'
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: theme.zIndex.modal,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.SlabGray[950],
        color: theme.palette.SlabGray[0],
        px: 4,
        py: 6,
        textAlign: 'center',
      }}
    >
      <IconButton
        aria-label='Dismiss banner'
        onClick={dismiss}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          color: theme.palette.SlabGray[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <Box component='img' src='/logo-words-grey.png' alt='Slabstack' sx={{ height: 56, mb: 6 }} />

      <Typography variant='h5' sx={{ fontWeight: 700, color: theme.palette.SlabGray[0], mb: 1.5 }}>
        Better in the app
      </Typography>
      <Typography variant='body2' sx={{ color: theme.palette.SlabGray[500], mb: 6, maxWidth: 280 }}>
        Get a faster, richer experience with the Slabstack mobile app.
      </Typography>

      <Button
        variant='slab-primary'
        size='large'
        fullWidth
        onClick={openInApp}
        sx={{ mb: 2, maxWidth: 320 }}
      >
        Open in App
      </Button>

      <Button
        variant='text'
        size='large'
        fullWidth
        onClick={dismiss}
        sx={{ color: theme.palette.SlabGray[500], maxWidth: 320 }}
      >
        Continue in Web
      </Button>
    </Box>
  );
};
